/**
 * Camera tags — the contract between the shared tag UI and each app's transport.
 *
 * Tags are admin-defined labels in categories ("Department" → Traffic, Health care;
 * "Placement" → Indoor, Outdoor) that the camera list filters on, plus the ONVIF PTZ flag.
 * They live on the NODE (backend routes/cameraTagRoutes.js). The local app calls its own
 * backend; the cloud calls /api/nodes/:nodeId/camera-tags, which reaches the same controller
 * through the tunnel's camera_tags_proxy. Both implement this interface, so the components in
 * this folder never know which they are talking to.
 */

export interface CameraTag {
  id: string;
  name: string;
  color: string | null;
  sort_order?: number;
  /** How many of the caller's cameras carry it (catalogue only). */
  camera_count?: number;
  category_id?: string;
  category_name?: string;
}

export interface CameraTagCategory {
  id: string;
  name: string;
  description: string | null;
  /** false = a camera may carry at most one tag from this category (Indoor OR Outdoor). */
  multi_select: boolean;
  sort_order: number;
  tags: CameraTag[];
}

export interface CameraTagCatalog {
  categories: CameraTagCategory[];
  /** Cameras by effective PTZ: manual override, else ONVIF detection, else unknown. */
  ptz: { yes: number; no: number; unknown: number };
  colors: string[];
}

export interface PtzDetectStatus {
  running: boolean;
  scope?: 'unknown' | 'all';
  total?: number;
  done?: number;
  ptz_found?: number;
  failed?: number;
  started_at?: string;
  finished_at?: string | null;
}

/** A tag as held by a filter selection — enough to label its chip without the catalogue. */
export interface SelectedCameraTag {
  id: string;
  name: string;
  color: string | null;
  category_id: string;
  category_name: string;
}

export type PtzFilter = '' | 'yes' | 'no' | 'unknown';

export interface CameraTagsApi {
  /**
   * Which catalogue this is — the cache key. 'local' on a node; the selected site's id in the
   * cloud, so switching site never shows the previous site's tags.
   */
  scopeKey(): string;
  catalog(): Promise<CameraTagCatalog>;
  createCategory(data: { name: string; description?: string; multi_select?: boolean }): Promise<any>;
  updateCategory(id: string, data: Partial<Pick<CameraTagCategory, 'name' | 'description' | 'multi_select' | 'sort_order'>>): Promise<any>;
  deleteCategory(id: string): Promise<any>;
  createTag(categoryId: string, data: { name: string; color?: string | null }): Promise<any>;
  updateTag(id: string, data: { name?: string; color?: string | null }): Promise<any>;
  deleteTag(id: string): Promise<any>;
  /** Add/remove tags on many cameras. A one-per-camera tag replaces the camera's current one. */
  bulkAssign(cameraIds: string[], addTagIds: string[], removeTagIds: string[]): Promise<{ updated: number; skipped: number }>;
  detectStatus(): Promise<PtzDetectStatus>;
  startDetect(scope: 'unknown' | 'all'): Promise<PtzDetectStatus>;
  /** Every camera the caller can see, each with `tags` and `groups` — for the per-tag picker. */
  listAllCameras(): Promise<any[]>;
}

/** The API's own message for a failed call, else a fallback. Handles both apps' error shapes. */
export function tagErrorMessage(err: any, fallback: string): string {
  const body = err?.response?.data;
  const nested = body?.error;
  return body?.error?.details?.[0]?.message
    || (typeof nested === 'string' ? nested : nested?.message)
    || body?.message
    || err?.message
    || fallback;
}

/** The PTZ filter's choices, in the order they are offered. */
export const PTZ_FILTER_OPTIONS: Array<{ value: PtzFilter; label: string }> = [
  { value: '', label: 'Any' },
  { value: 'yes', label: 'PTZ' },
  { value: 'no', label: 'Fixed' },
  { value: 'unknown', label: 'Not detected' },
];

/** How an active PTZ filter is named on its chip. */
export const PTZ_FILTER_LABEL: Record<string, string> = {
  yes: 'PTZ cameras', no: 'Fixed cameras', unknown: 'PTZ not detected',
};
